import React, { useState, useEffect, useMemo } from 'react';
import { useAlert, ALERT_TYPES } from '@etiquette-ui/alert-dialog';

import IArea from '@appsinti/bigbrother-frontend/interfaces/IArea';
import IGroupedOption, { Option } from '@appsinti/ui-core/dropdown/interfaces/IGroupOption';
import MultiGroupedDropdown from '@appsinti/ui-core/dropdown/MultiGroupedDropdown';

import { getOptions, getSelectedOptions } from '@appsinti/bigbrother-frontend/utils/options';
import ReplaceHomeCameraDialog from '../dialogs/replaceHomeCameraDialog';
import InfoDialog from '@appsinti/ui-core/dialogs/InfoDialog';
import ConfirmDialog from '@appsinti/ui-core/dialogs/ConfirmDialog';

import useI18n from '@appsinti/i18n/useI18n';
import { modifyPreviewCameras } from '../../../services/cameras';
import IVideo from '../../../interfaces/IVideo';
import { useData } from '../../data';
import { resolveChangeAction, ChangeAction } from './resolveChangeAction';

const PreviewCamerasManager: React.FC<{
  areas: IArea[],
  appId: number,
  previews: IVideo[]
}> = ({ areas, appId, previews }) => {

  const { translate, translateObject } = useI18n();
  const { fetchData } = useData();
  const [selectedOptions, setSelectedOptions] = useState<Option[]>();
  const alert = useAlert();

  const options: IGroupedOption[] = useMemo(() => {
    if (!areas.length) return [];
    return getOptions(areas, translateObject);
  }, [areas]);

  const storedOptions = () => getSelectedOptions(options, previews.map(preview => preview.id));

  useEffect(() => {
    setSelectedOptions(storedOptions());
  }, [previews, options]);

  const showUpdateFailed = () => {
    alert({
      type: ALERT_TYPES.CUSTOM,
      config: {
        props: {
          title: translate('Action required'),
          textBody: translate('The upgrade failed, try again later'),
          closeButtonText: translate('Close')
        },
        body: InfoDialog,
      }
    });
  };

  const changeOptions = async (
    newOptions: Option[] | undefined,
    { notifyFailure = true }: { notifyFailure?: boolean } = {}
  ): Promise<boolean> => {
    if (!newOptions) return false;

    setSelectedOptions(newOptions);
    const success = await modifyPreviewCameras(newOptions.map(option => option.value), appId);

    if (success) {
      await fetchData();
      return true;
    }

    if (notifyFailure) showUpdateFailed();
    setSelectedOptions(storedOptions());
    return false;
  };

  const showDialog = (action: ChangeAction) => {
    switch (action.type) {
      case 'low_limit':
        alert({
          type: ALERT_TYPES.CUSTOM,
          config: {
            props: {
              title: translate('Action required'),
              textBody: translate('You need to highlight other camera to replace this one'),
              closeButtonText: translate('Close')
            },
            body: InfoDialog,
          }
        });
        break;
      case 'delete':
        alert({
          type: ALERT_TYPES.CUSTOM,
          config: {
            props: {
              title: translate('Action required'),
              textBody: translate('Click on Update to delete the camera assigned to appsinti home'),
              confirmButtonText: translate('Update'),
              closeButtonText: translate('Close'),
              onSubmit: () => changeOptions(action.checkedOptions),
            },
            body: ConfirmDialog,
          }
        })
          .catch(() => {
          });
        break;
      case 'add':
        alert({
          type: ALERT_TYPES.CUSTOM,
          config: {
            props: {
              title: translate('Action required'),
              textBody: translate('Click on Update to set the camera as assigned to appsinti home'),
              confirmButtonText: translate('Update'),
              closeButtonText: translate('Close'),
              onSubmit: () => changeOptions(action.checkedOptions)
            },
            body: ConfirmDialog,
          },
        })
          .catch(() => {
          });
        break;
      case 'replace':
        alert({
          type: ALERT_TYPES.CUSTOM,
          config: {
            props: {
              newOption: action.newOption,
              selectedOptions,
              setSelectedOption: (options: Option[] | undefined) =>
                changeOptions(options, { notifyFailure: false }),
            },
            body: ReplaceHomeCameraDialog,
          }
        });
        break;
    }
  };

  const onChange = (checkedOptions: Option[]) => {
    showDialog(resolveChangeAction(selectedOptions ?? [], checkedOptions));
  };

  return (
    <>{(!!options && !!selectedOptions) && (
      <MultiGroupedDropdown
        placeholder={translate('Select...')}
        label={translate('Appsinti home cameras')}
        options={options}
        value={selectedOptions}
        onChange={onChange}
      />
    )}
    </>
  );
};
export default PreviewCamerasManager;
